// Utility functions for categorizing players into ELO-based buckets
// Used by matchmaking to avoid pairing players with large skill gaps

const DEFAULT_MIN_PLAYERS = 5;
const MAX_CATEGORIES = 4;
const DEFAULT_ELO = 1200;

/**
 * Adaptive max-buckets for gender cohorts to avoid tiny buckets with only 2–3
 * players (which forces A–D spans on nearly every women's combo).
 * Mixed cohort always uses the maximum 4 buckets.
 *
 * | Players in cohort | Max buckets |
 * |-------------------|-------------|
 * | 5–7               | 2 (A/B)     |
 * | 8–11              | 3 (A/B/C)   |
 * | 12+               | 4 (A/B/C/D) |
 */
function getAdaptiveMaxBuckets(playerCount, cohortType) {
  if (cohortType === 'mixed') return MAX_CATEGORIES;
  if (playerCount < 8) return 2;
  if (playerCount < 12) return 3;
  return MAX_CATEGORIES;
}

const CATEGORY_LABELS = ['A', 'B', 'C', 'D'];

const getGender = (gender) => (gender || '').toLowerCase();

const getDefaultMetadata = (overrides = {}) => ({
  categories: {},
  categoryRanges: {},
  categoryCount: 0,
  lastCalculated: null,
  disabledReason: null,
  stats: null,
  breakpoints: [],
  method: null,
  playerCount: 0,
  cohortType: 'mixed',
  ...overrides,
});

function calculateStats(values = []) {
  if (!values.length) {
    return { mean: 0, stdDev: 0, min: 0, max: 0, spread: 0 };
  }

  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const variance = values.reduce((sum, val) => sum + (val - mean) ** 2, 0) / values.length;
  const stdDev = Math.sqrt(variance);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const spread = max - min;

  return { mean, stdDev, min, max, spread };
}

function getEloValue(player, cohortType = 'mixed') {
  const raw =
    cohortType === 'mixed' ? player?.mixed_elo_rating : player?.levels_elo_rating;
  const parsed = Number(raw);
  if (Number.isFinite(parsed)) {
    return parsed;
  }
  return DEFAULT_ELO;
}

function buildSegments(sortedPlayers, breakpoints) {
  if (!sortedPlayers.length) return [];
  const validBreakpoints = Array.from(new Set(breakpoints))
    .filter(idx => idx > 0 && idx < sortedPlayers.length)
    .sort((a, b) => a - b);

  const segments = [];
  let start = 0;
  validBreakpoints.forEach(bp => {
    const slice = sortedPlayers.slice(start, bp);
    if (slice.length) {
      segments.push({ players: slice });
    }
    start = bp;
  });
  const tail = sortedPlayers.slice(start);
  if (tail.length) {
    segments.push({ players: tail });
  }
  return segments.length ? segments : [{ players: sortedPlayers }];
}

function computeSegmentStats(segmentPlayers) {
  const elos = segmentPlayers.map(p => p.elo);
  return {
    players: segmentPlayers,
    min: Math.min(...elos),
    max: Math.max(...elos),
    size: segmentPlayers.length,
    avg: elos.reduce((sum, val) => sum + val, 0) / segmentPlayers.length,
  };
}

function categorizeCohort(players = [], { cohortType = 'mixed' } = {}) {
  const result = getDefaultMetadata({
    cohortType,
    lastCalculated: new Date().toISOString(),
  });

  if (!players.length) {
    result.disabledReason = 'no_players';
    return result;
  }

  const processed = players
    .filter(player => player?.id)
    .map(player => ({
      playerId: player.id,
      elo: getEloValue(player, cohortType),
    }));

  result.playerCount = processed.length;

  if (processed.length < DEFAULT_MIN_PLAYERS) {
    result.disabledReason = 'not_enough_players';
    return result;
  }

  const sortedPlayers = processed.sort((a, b) => b.elo - a.elo);
  const stats = calculateStats(sortedPlayers.map(p => p.elo));
  result.stats = stats;

  const maxBuckets = getAdaptiveMaxBuckets(processed.length, cohortType);
  const adaptiveFractions = [];
  for (let i = 1; i < maxBuckets; i++) {
    adaptiveFractions.push(i / maxBuckets);
  }
  const breakpoints = adaptiveFractions
    .map(fraction => Math.floor(sortedPlayers.length * fraction))
    .filter(idx => idx > 0 && idx < sortedPlayers.length);

  const segments = buildSegments(sortedPlayers, breakpoints).slice(0, maxBuckets);
  const populatedSegments = segments.map(segment => computeSegmentStats(segment.players));

  populatedSegments.forEach((segment, index) => {
    const label = CATEGORY_LABELS[index] || CATEGORY_LABELS[CATEGORY_LABELS.length - 1];
    segment.players.forEach(player => {
      result.categories[player.playerId] = label;
    });
    result.categoryRanges[label] = {
      min: segment.min,
      max: segment.max,
      size: segment.size,
    };
  });

  result.categoryCount = populatedSegments.length;
  result.method = 'quartiles';
  result.breakpoints = breakpoints;

  if (result.categoryCount === 0) {
    result.disabledReason = 'insufficient_breaks';
  }

  return result;
}

export function categorizePlayersForSession(players = [], options = {}) {
  const mixed = categorizeCohort(players, { ...options, cohortType: 'mixed' });
  const malePlayers = players.filter(player => getGender(player.gender) === 'male');
  const femalePlayers = players.filter(player => getGender(player.gender) === 'female');

  const levelsMale = categorizeCohort(malePlayers, { ...options, cohortType: 'levels_male' });
  const levelsFemale = categorizeCohort(femalePlayers, { ...options, cohortType: 'levels_female' });

  return {
    mixed,
    levels_male: levelsMale,
    levels_female: levelsFemale,
  };
}

export function categorizePlayersByElo(players = [], eloType = 'mixed', options = {}) {
  if (eloType === 'mixed') {
    return categorizeCohort(players, { ...options, cohortType: 'mixed' });
  }

  if (eloType === 'levels') {
    const malePlayers = players.filter(player => getGender(player.gender) === 'male');
    const femalePlayers = players.filter(player => getGender(player.gender) === 'female');

    const maleResult = categorizeCohort(malePlayers, { ...options, cohortType: 'levels_male' });
    const femaleResult = categorizeCohort(femalePlayers, { ...options, cohortType: 'levels_female' });

    return {
      categories: {
        ...(maleResult.categories || {}),
        ...(femaleResult.categories || {}),
      },
      categoryRanges: {
        male: maleResult.categoryRanges || {},
        female: femaleResult.categoryRanges || {},
      },
      categoryCount: Math.max(maleResult.categoryCount || 0, femaleResult.categoryCount || 0),
      stats: {
        male: maleResult.stats,
        female: femaleResult.stats,
      },
      lastCalculated: maleResult.lastCalculated || femaleResult.lastCalculated,
      disabledReason: maleResult.disabledReason && femaleResult.disabledReason
        ? 'not_enough_players'
        : null,
    };
  }

  return categorizeCohort(players, { ...options, cohortType: 'mixed' });
}

/**
 * Compute absolute gap between two category labels (A, B, C, D)
 */
export function getCategoryGap(category1, category2) {
  if (!category1 || !category2) {
    return Infinity;
  }

  const idx1 = CATEGORY_LABELS.indexOf(category1);
  const idx2 = CATEGORY_LABELS.indexOf(category2);

  if (idx1 === -1 || idx2 === -1) {
    return Infinity;
  }

  return Math.abs(idx1 - idx2);
}

/**
 * Determine if categories are compatible
 * - gap 0 (same group): allowed, no penalty
 * - gap 1 (e.g., D and C): allowed, small penalty
 * - gap 2 (e.g., D and B): allowed, bigger penalty
 * - gap 3+ (e.g., A and D): not allowed, hard block
 */
export function areCategoriesCompatible(category1, category2) {
  const gap = getCategoryGap(category1, category2);
  if (gap === Infinity) {
    return { allowed: false, gap };
  }

  // Gap 3+ is a hard block
  if (gap >= 3) {
    return { allowed: false, gap };
  }

  // Gaps 0, 1, and 2 are allowed (penalties applied in evaluateCategoryMatch)
  return { allowed: true, gap };
}

export default {
  categorizePlayersForSession,
  categorizePlayersByElo,
  getCategoryGap,
  areCategoriesCompatible,
};

